// ============================================================
// Die Freigabe neuer Projekte (SPEC §36) — eine Leseroute, drei Änderungen.
//
// Jede Rolle darf ein Projekt anlegen; was die Mitarbeit anlegt, startet
// `offen` und steht erst nach der Freigabe im Haus. Seit der Rückfrage vom
// 29.09.2026 geht die Anfrage NUR an die Projektleitung des Projekts (die die
// Mitarbeit beim Anlegen wählen muss); freigeben dürfen außer ihr nur
// Geschäftsführung und Admin, und die werden nicht gerufen. Hier stehen die
// Wege dazu und die eine Zahl, die Kopfleiste, Mobilleiste und Startseite
// zeigen — sie zählt nur, was bei der fragenden Person als Projektleitung liegt.
//
// Eine eigene Datei neben `hooks.ts` aus demselben Grund wie
// `api/startseite.ts`: `hooks.ts` ist die meistgeänderte Datei des
// Frontends. Die Regeln aus ihrem Kopf gelten hier genauso — Serverdaten
// gehören TanStack Query, Pfade ohne `/api`, nach jeder Änderung `entwerten`,
// ein 409 legt den mitgelieferten Projektstand ab und wirft weiter.
//
// Der Schlüssel `schluessel.freigaben` steht in `hooks.ts` (dort entwertet
// ihn `useProjektNeu`), und `lib/ereignisse.ts` entwertet ihn bei jeder
// Meldung von `projekt`, `projekt_buero` und `projekt_person` — eine Freigabe,
// die jemand anderes erteilt, verschwindet so ohne Neuladen aus der Karte, und
// eine Anfrage, die mit der Leitung wandert (Teamzeile `leitung`, §36,
// 29.09.2026), kommt bei der neuen an und geht bei der alten.
// ============================================================
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api, istApiFehler } from "./client";
import { aktuellesSchluessel, entwerten, ohneDetail, schluessel } from "./hooks";
import type { FreigabeAblehnen, FreigabeListe, Projekt } from "./types";
import { useUi } from "../store/ui";

/** Die Grenze für den Ablehnungsgrund — dieselbe Zahl wie die CHECK-Bedingung in
 *  Wanderung 039 und die Prüfung im Router. Wer sie ändert, ändert alle drei. */
export const FREIGABE_GRUND_MAX = 500;

/** Die leere Antwort: für Abgemeldete, und solange nichts geladen ist. */
export const KEINE_FREIGABEN: FreigabeListe = { warten: [], eigene: [] };

/**
 * Wie viele Freigaben verlangen etwas von der fragenden Person? Das ist die
 * Zahl am Punkt der Kopfleiste und im Kontomenü.
 *
 * `warten` ganz — darüber entscheidet sie. Von `eigene` nur die ABGELEHNTEN:
 * die muss sie überarbeiten und erneut einreichen oder löschen. Ein eigenes
 * Projekt, das noch `offen` ist, verlangt nichts von ihr; es steht in der
 * Karte auf der Startseite, aber ein Punkt dafür wäre ein Wecker, der
 * klingelt, obwohl niemand etwas tun kann.
 */
export function freigabenZahl(liste: FreigabeListe | null | undefined): number {
  if (!liste) return 0;
  return liste.warten.length + liste.eigene.filter((e) => e.freigabe === "abgelehnt").length;
}

/**
 * `GET /freigaben`. `aktiv` wie überall: abgemeldet wird nicht gefragt.
 *
 * Eine kleine Antwort, die Kopfleiste, Mobilleiste und Startseite teilen —
 * TanStack Query fragt sie für alle drei einmal. Frisch gehalten wird sie vom
 * Ereignisstrom (`lib/ereignisse.ts`). Das Netz darunter ist das Nachfragen alle
 * zwei Minuten, wie beim Punkt an „Aktuelles" (`useNeuesteBeitraege`): verbindet
 * der Strom neu, gehen die Meldungen der Lücke verloren, und `staleTime` allein
 * fragt nie von selbst — die Kopfleiste ist immer eingehängt, und Fokuswechsel
 * holen nichts (`refetchOnWindowFocus: false`, main.tsx). Ohne das stünde der
 * Punkt nach einer Lücke bis zum Neuladen falsch.
 */
export function useFreigaben(aktiv = true): UseQueryResult<FreigabeListe> {
  return useQuery({
    queryKey: schluessel.freigaben,
    queryFn: () => api.get<FreigabeListe>("/freigaben"),
    enabled: aktiv,
    staleTime: 60_000,
    refetchInterval: 120_000,
    // Nachts auf einem Zweitschirm soll niemand das Backend wecken.
    refetchIntervalInBackground: false,
  });
}

/**
 * Wie `mitKonflikt` in `hooks.ts`, für die drei Freigabewege: ein 409 heißt
 * hier „darüber ist schon entschieden" (jemand anderes war schneller, oder die
 * anlegende Person hat gerade selbst etwas geändert). Der Körper ist wie bei
 * jedem 409 eines Projekts der aktuelle `Projekt` samt `detail` — er kommt in
 * den Zwischenspeicher, damit das Band im Modal sofort den wahren Stand zeigt,
 * und der Fehler geht weiter an den Toast.
 */
async function mitStand(qc: QueryClient, projektId: string, fn: () => Promise<Projekt>): Promise<Projekt> {
  try {
    return await fn();
  } catch (e) {
    if (istApiFehler(e) && e.status === 409 && istProjekt(e.body)) {
      qc.setQueryData(schluessel.projekt(projektId), ohneDetail(e.body));
      void qc.invalidateQueries({ queryKey: schluessel.stand });
    }
    throw e;
  }
}

function istProjekt(body: unknown): body is Projekt {
  return !!body && typeof body === "object" && typeof (body as { id?: unknown }).id === "string" && typeof (body as { version?: unknown }).version === "number";
}

/** Nach jedem der drei Wege: das Projekt ablegen, Stand und Liste entwerten. */
function ablegen(qc: QueryClient, p: Projekt): void {
  qc.setQueryData(schluessel.projekt(p.id), p);
  entwerten(qc, p.id);
}

/**
 * `POST /projekte/{id}/freigabe` → `Projekt`. Nur wer DIESES Projekt freigeben
 * darf (`Projekt.darf_freigeben`: seine Projektleitung, GF, Admin), nur aus `offen`.
 *
 * Erst JETZT legt der Server den Erfolgsbeitrag „Neues Projekt: …" an (§36.1,
 * vorher §16.3 beim Anlegen) — darum steht die Feier hier und nicht in
 * `useProjektNeu`, und Aktuelles wird entwertet.
 */
export function useProjektFreigeben(projektId: string): UseMutationResult<Projekt, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => mitStand(qc, projektId, () => api.post<Projekt>(`/projekte/${projektId}/freigabe`)),
    onSuccess: (p) => {
      ablegen(qc, p);
      useUi.getState().feiern({ art: "projekt", titel: p.name, untertitel: p.ort });
      void qc.invalidateQueries({ queryKey: aktuellesSchluessel.alle });
    },
    // Auch nach einem Fehlschlag: ein 404 (inzwischen gelöscht) oder ein 409
    // (schon entschieden) heißt, dass die Liste nicht mehr stimmt.
    onSettled: () => void qc.invalidateQueries({ queryKey: schluessel.freigaben }),
  });
}

/** `POST /projekte/{id}/freigabe/ablehnen {grund}` → `Projekt`. Wie Freigeben; `grund` ist Pflicht. */
export function useProjektAblehnen(projektId: string): UseMutationResult<Projekt, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grund: string) =>
      mitStand(qc, projektId, () => api.post<Projekt>(`/projekte/${projektId}/freigabe/ablehnen`, { grund } satisfies FreigabeAblehnen)),
    onSuccess: (p) => ablegen(qc, p),
    onSettled: () => void qc.invalidateQueries({ queryKey: schluessel.freigaben }),
  });
}

/**
 * `POST /projekte/{id}/freigabe/erneut` → `Projekt`. Nur die anlegende Person,
 * nur aus `abgelehnt`, nur mit einer Projektleitung, die freigeben kann (sonst
 * 422 `LEITUNG_PFLICHT`); danach wieder `offen`, und die JETZIGE Projektleitung
 * bekommt erneut Post. Löschen statt Einreichen ist der gewöhnliche
 * `DELETE /projekte/{id}` (`useProjektLoeschen`) — die anlegende Person darf
 * ihr nicht freigegebenes Projekt bearbeiten, also auch löschen.
 */
export function useProjektErneut(projektId: string): UseMutationResult<Projekt, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => mitStand(qc, projektId, () => api.post<Projekt>(`/projekte/${projektId}/freigabe/erneut`)),
    onSuccess: (p) => ablegen(qc, p),
    onSettled: () => void qc.invalidateQueries({ queryKey: schluessel.freigaben }),
  });
}
